/**
 * Utility functions for hashrate conversion and formatting
 * Centralized module for all hashrate, number, and time formatting operations
 */

export interface HashrateUnit {
  value: number
  unit: string
}

export interface NumberWithUnit {
  value: number
  unit: string
}

/**
 * Standard unit configurations for different measurement types
 */
const HASHRATE_UNITS = [
  { threshold: 1e12, unit: 'EH/s' },
  { threshold: 1e9, unit: 'PH/s' },
  { threshold: 1e6, unit: 'TH/s' },
  { threshold: 1e3, unit: 'GH/s' },
  { threshold: 1, unit: 'MH/s' }
] as const

const NUMBER_UNITS = [
  { threshold: 1e15, unit: 'P' },
  { threshold: 1e12, unit: 'T' },
  { threshold: 1e9, unit: 'B' },
  { threshold: 1e6, unit: 'M' },
  { threshold: 1e3, unit: 'K' }
] as const

/**
 * Validates if a number is safe for calculations
 */
const isValidNumber = (value: number): boolean => {
  return isFinite(value) && !isNaN(value)
}

/**
 * Validates if a number is positive and safe for calculations
 */
const isValidPositiveNumber = (value: number): boolean => {
  return isValidNumber(value) && value >= 0
}

/**
 * Generic function to format numbers with appropriate units
 */
const formatWithUnits = (
  value: number,
  units: readonly { threshold: number; unit: string }[],
  fallbackUnit: string,
  decimals = 2
): NumberWithUnit => {
  if (!isValidPositiveNumber(value)) {
    return { value: 0, unit: fallbackUnit }
  }

  for (const { threshold, unit } of units) {
    if (value >= threshold) {
      return {
        value: Number((value / threshold).toFixed(decimals)),
        unit
      }
    }
  }

  return { value: Number(value.toFixed(decimals)), unit: fallbackUnit }
}

/**
 * Convert MHash/s to appropriate unit (TH/s, PH/s, EH/s)
 */
export function convertFromMhash(mhashPerSec: number): HashrateUnit {
  return formatWithUnits(mhashPerSec, HASHRATE_UNITS, 'MH/s')
}

/**
 * Format hashrate value for display
 */
export function formatHashrate(mhashPerSec: number): string {
  // For hashrate display, negative values should be treated as zero
  if (mhashPerSec < 0) {
    return '0.00 MH/s'
  }
  
  const { value, unit } = convertFromMhash(mhashPerSec)
  const formattedValue = value < 10 ? value.toFixed(2) : value.toFixed(1)
  return `${formattedValue} ${unit}`
}

/**
 * Convert hashrate string/number to MHash/s
 */
export function parseHashrate(hashrate: string | number): number {
  if (typeof hashrate === 'string') {
    const parsed = parseFloat(hashrate)
    return isValidNumber(parsed) ? parsed : 0
  }
  return isValidNumber(hashrate) ? hashrate : 0
}

/**
 * Format large numbers with appropriate suffixes (K, M, B, T, P)
 */
export const formatLargeNumber = (value: number): string => {
  if (!value || value === 0 || !isValidPositiveNumber(value)) return '0'
  
  // Handle very small numbers - show precise values in scientific notation for readability
  if (Math.abs(value) < 0.001) {
    if (Math.abs(value) < 1e-15) {
      return '0'
    }
    // Show precise scientific notation for very small values
    return value.toExponential(2)
  }
  
  const { value: formattedValue, unit } = formatWithUnits(value, NUMBER_UNITS, '', 2)
  
  if (unit) {
    return `${formattedValue.toFixed(2)}${unit}`
  }
  
  // For values < 1000, show appropriate decimal places
  if (Math.abs(value) >= 1) {
    return value.toFixed(2)
  }
  
  return value.toFixed(6)
}

/**
 * Format block count as whole number
 */
export const formatBlockCount = (count: number): string => {
  if (!isValidPositiveNumber(count)) return '0'
  
  const { value: formattedValue, unit } = formatWithUnits(count, NUMBER_UNITS, '')
  
  if (unit) {
    // For large numbers with units, don't show decimal places for block count
    return `${Math.round(formattedValue)}${unit}`
  }
  
  // For smaller numbers, show as whole number
  return Math.round(count).toString()
}

/**
 * Format difficulty numbers with appropriate units
 */
export const formatDifficulty = (difficulty: number): string => {
  if (!isValidPositiveNumber(difficulty)) return '0'
  
  // For very small difficulty values, show precise scientific notation
  if (Math.abs(difficulty) < 0.001) {
    if (Math.abs(difficulty) < 1e-15) {
      return '0'
    }
    return difficulty.toExponential(2)
  }
  
  return formatLargeNumber(difficulty)
}

/**
 * Format participant count
 */
export const formatParticipants = (count: number): string => {
  if (!isValidPositiveNumber(count)) return '0'
  return Math.round(count).toLocaleString()
}

/**
 * Format Bitcoin amount with appropriate decimal places
 */
export const formatBitcoinAmount = (amount: number): string => {
  if (!isValidPositiveNumber(amount)) return '0.00'
  
  // Dynamic decimal places based on amount size
  if (amount >= 1) return amount.toFixed(2)
  if (amount >= 0.01) return amount.toFixed(4)
  return amount.toFixed(8)
}

/**
 * Format time in seconds to human readable format
 */
export const formatTimeFromSeconds = (seconds: number): string => {
  if (!isValidPositiveNumber(seconds)) return '0 seconds'
  
  // Handle very small numbers
  if (Math.abs(seconds) < 0.001) {
    return '0 seconds'
  }
  
  const timeUnits = [
    { threshold: 86400, unit: 'd', next: 3600 },
    { threshold: 3600, unit: 'h', next: 60 },
    { threshold: 60, unit: 'm', next: 1 },
    { threshold: 1, unit: 's', next: 0 }
  ]
  
  for (const { threshold, unit, next } of timeUnits) {
    if (seconds >= threshold) {
      const mainValue = Math.floor(seconds / threshold)
      
      if (next > 0) {
        const remainder = Math.floor((seconds % threshold) / next)
        const nextUnit = timeUnits.find(u => u.threshold === next)?.unit
        
        if (remainder > 0 && nextUnit) {
          return `${mainValue}${unit} ${remainder}${nextUnit}`
        }
      }
      
      return `${mainValue}${unit}`
    }
  }
  
  return `${Math.round(seconds)}s`
}

/**
 * Format time in seconds to hours for mining pool display
 */
export const formatBlockTimeHours = (seconds: number): string => {
  if (!isValidNumber(seconds)) return '0 hours'
  
  // Handle very small numbers - essentially zero
  if (Math.abs(seconds) < 1e-10) {
    return '< 1 second'
  }
  
  const hours = seconds / 3600
  
  if (hours >= 24) {
    const days = Math.floor(hours / 24)
    const remainingHours = Math.round(hours % 24)
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
  }
  
  if (hours >= 1) {
    return `${Math.round(hours)} hours`
  }
  
  const minutes = Math.round(seconds / 60)
  return minutes > 0 ? `${minutes} minutes` : `${Math.round(seconds)} seconds`
}

/**
 * Calculate shares in reward window from API data
 * Formats the number of shares in the current reward window
 */
export const calculateSharesInWindow = (shares: number): string => {
  if (!isValidNumber(shares) || shares === 0) return '0'
  
  // Format shares with appropriate units (K, M, B, T, P)
  const { value, unit } = formatWithUnits(shares, NUMBER_UNITS, '')
  return unit ? `${value}${unit}` : shares.toString()
}

/**
 * Calculate estimated rewards from API data
 * Based on pool's PPLNS (Pay Per Last N Shares) reward system
 * Formula: (miner_shares_in_window / share_log_window) × current_block_reward × (1 - pool_fee)
 */
export const calculateEstimatedRewards = (
  totalAmount: string, 
  shares: number, 
  shareLogWindow?: number,
  poolFee: number = 0.02, // Default 2% pool fee
  currency: string = 'BTC' // Currency symbol
): string => {
  const amount = parseFloat(totalAmount || '0')
  if (!isValidNumber(amount) || amount === 0 || !isValidNumber(shares) || shares === 0) {
    return `0.00000000 ${currency}`
  }
  
  // Use a reasonable default for share_log_window if not provided
  const window = shareLogWindow || 8 * 100000000000 // 8 times network difficulty (approximate)
  
  // Calculate estimated reward based on PPLNS formula
  // This is the proportion of shares in the window times the block reward minus pool fee
  const sharePercentage = shares / window
  const blockReward = 6.25 // Current Bitcoin block reward
  const estimatedReward = sharePercentage * blockReward * (1 - poolFee)
  
  return `${Math.max(0, estimatedReward).toFixed(8)} ${currency}`
}

/**
 * Calculate estimated earnings next block from API data
 * Based on current hashrate contribution and estimated block time
 * Formula: (miner_hashrate / pool_total_hashrate) × block_reward × (1 - pool_fee)
 */
export const calculateEstimatedEarnings = (
  minerHashrate: number,
  poolTotalHashrate: number,
  poolFee: number = 0.02, // Default 2% pool fee
  currency: string = 'BTC' // Currency symbol
): string => {
  if (!isValidNumber(minerHashrate) || !isValidNumber(poolTotalHashrate) || 
      minerHashrate === 0 || poolTotalHashrate === 0) {
    return `0.00000000 ${currency}`
  }
  
  // Calculate miner's percentage of total pool hashrate
  const hashratePercentage = minerHashrate / poolTotalHashrate
  
  // Current Bitcoin block reward
  const blockReward = 6.25
  
  // Calculate estimated earnings for next block
  const estimatedEarning = hashratePercentage * blockReward * (1 - poolFee)
  
  return `${Math.max(0, estimatedEarning).toFixed(8)} ${currency}`
}

 